import turfDoesIntersect from '@turf/boolean-intersects';
import turfUnion from '@turf/union';
import turfDoesContain from '@turf/boolean-contains';
import turfIntersect from '@turf/intersect';
import turfDifference from '@turf/difference';
import turfIsValid from '@turf/boolean-valid';
import turfKinks from '@turf/kinks';
import { MultiPolygon, Polygon, type SimpleGeometry } from 'ol/geom';

import { GeometryType, isPolygonAssignable } from 'cadenza/map/geom/geometry-type';

import type { Geometry as TurfGeometry, MultiPolygon as TurfMultiPolygon, Polygon as TurfPolygon } from '@turf/helpers';

export class UnionWithoutIntersectionStrategy {

  isApplicable (newGeometry: SimpleGeometry): boolean {
    const geometryType = newGeometry.getType();
    return isPolygonAssignableOrMultiPolygon(geometryType);
  }

}

export function getMergedGeometry (currentGeometries: (Polygon | MultiPolygon)[], newGeometry: Polygon | MultiPolygon) {
  // If there's more than 1 geometry originally, combine them into a multiPolygon
  const currentGeometry = combineGeometries(currentGeometries);
  return getMultiPolygonWithHoles(currentGeometry, newGeometry);
}

export function isPolygonAssignableOrMultiPolygon (geometryType: GeometryType) {
  return isPolygonAssignable(geometryType) || geometryType === GeometryType.MULTI_POLYGON;
}

/**
 * Calculates the symmetrical difference between 2 (Multi)Polygons
 *
 * @param currentGeometry
 * @param newGeometry
 * @return - a MultiPolygon representing the symmetrical difference between the currentGeometry and the newGeometry.
 *         - null if the currentGeometry and the newGeometry are exactly the same, or if they have no point in common
 *         and the new geometry is not contained in the outerRing of the currentGeometry.
 */
function getMultiPolygonWithHoles (currentGeometry: Polygon | MultiPolygon, newGeometry: Polygon | MultiPolygon): MultiPolygon | null {
  const turfCurrentGeometry = toTurfPolygonOrMultiPolygon(currentGeometry);
  const turfNewGeometry = toTurfPolygon(newGeometry);

  if (turfDoesIntersect(turfCurrentGeometry, turfNewGeometry)) {
    const symetricalDifference = getSymetricalDifference(turfCurrentGeometry, turfNewGeometry);
    return symetricalDifference ? multiPolygonFromTurfGeometry(symetricalDifference) : null;
  } else if (doesCurrentGeometryOuterRingContainGeometry(currentGeometry, turfNewGeometry)) { // If we draw in an island
    return multiPolygonFromTurfGeometry(turfUnion(turfCurrentGeometry, turfNewGeometry)!.geometry);
  }
  return null;
}

function toTurfPolygon (newGeometry: Polygon | MultiPolygon): TurfPolygon {
  // Because of the draw geometryType, we could end up with a MultiPolygon geometry here, but this will be always formed of 1 polygon
  if (newGeometry instanceof MultiPolygon) {
    return toTurfPolygonOrMultiPolygon(newGeometry.getPolygon(0)) as TurfPolygon;
  }
  return toTurfPolygonOrMultiPolygon(newGeometry) as TurfPolygon;
}

function toTurfPolygonOrMultiPolygon (geometry: Polygon | MultiPolygon): TurfPolygon | TurfMultiPolygon {
  return turfGeometry(geometry.getType(), geometry.getCoordinates()) as TurfPolygon | TurfMultiPolygon;
}

function turfGeometry (type: string, coordinates: number[][][] | number[][][][]): TurfGeometry {
  return { type, coordinates };
}

// turf::boolean-contains doesn't work for MultiPolygon, so need to split the currentGeometry into Polygons
function doesCurrentGeometryOuterRingContainGeometry (olCurrentGeometry: Polygon | MultiPolygon, turfNewGeometry: TurfPolygon) {
  const currentGeometryPolygons = getPolygons(olCurrentGeometry);
  const turfOuterRingPolygons = currentGeometryPolygons.map(polygon => turfGeometry(GeometryType.POLYGON, [ polygon.getLinearRing(0)!.getCoordinates() ]));
  return turfOuterRingPolygons.some(turfOuterRingPolygon => turfDoesContain(turfOuterRingPolygon, turfNewGeometry));
}

export function combineGeometries (currentGeometries: (Polygon | MultiPolygon)[]) {
  return currentGeometries.length === 1
    ? currentGeometries[0]
    : new MultiPolygon(currentGeometries.flatMap(getPolygons));
}

function getPolygons (geometry: Polygon | MultiPolygon) {
  return geometry instanceof MultiPolygon ? geometry.getPolygons() : [ geometry ];
}

function multiPolygonFromTurfGeometry (geometry: TurfPolygon | TurfMultiPolygon): MultiPolygon {
  return (geometry.type === 'Polygon')
    ? new MultiPolygon([ geometry.coordinates ])
    : new MultiPolygon(geometry.coordinates);
}

function getSymetricalDifference (currentGeometry: TurfPolygon | TurfMultiPolygon, newGeometry: TurfPolygon | TurfMultiPolygon): TurfPolygon | TurfMultiPolygon | null {
  // We know the union is not null, if one of the geometries is not null, and we get here after drawing something, so not null
  const union = turfUnion(currentGeometry, newGeometry)!;
  const intersection = turfIntersect(currentGeometry, newGeometry);

  if (intersection) {
    const symetricalDifference = turfDifference(union, intersection);
    return symetricalDifference?.geometry ?? null;
  }
  return union.geometry;
}

export function areModificationsValid (geometriesBeingModified: (Polygon | MultiPolygon)[], allOtherGeometries: (Polygon | MultiPolygon)[]) {
  const allOtherTurfGeometries = allOtherGeometries.map(toTurfPolygonOrMultiPolygon);
  return geometriesBeingModified.every(geometry => isGeometryValid(geometry, allOtherTurfGeometries));
}

function isGeometryValid (modifiedGeometry: Polygon | MultiPolygon, allOtherTurfGeometries: (TurfPolygon | TurfMultiPolygon)[]): boolean {
  // Turf only checks intersection of the hole with the outerRing + some other small stuff,
  // we need to also check intersection of inner linear rings between themselves
  const isModifiedGeometryValid = isValidPolygonMultiPolygon(modifiedGeometry) && !doInnerLinearRingsIntersect(modifiedGeometry);
  if (isModifiedGeometryValid) {
    const turfModifiedGeometry = toTurfPolygonOrMultiPolygon(modifiedGeometry);
    return allOtherTurfGeometries.every(otherTurfGeom => !turfDoesIntersect(turfModifiedGeometry, otherTurfGeom));
  }
  return false;
}

// For Polygon turfIsValid does everything we want, but for MultiPolygon, it does checkPolygonAgainstOthers.
// which looks for the intersection of the polygons which form the MultiPolygon, this fails even if the intersection is 1 point.
// We don't want this to fail in this case, so we need to implement our own check.
// Since the difference between Polygon and MultiPolygon is only the checkPolygonAgainstOthers, we only need to implement this
// and run the Polygon check for all the Polygon in a MultiPolygon
function isValidPolygonMultiPolygon (geometry: Polygon | MultiPolygon) {
  if (geometry instanceof Polygon) {
    return isValidPolygon(geometry);
  }
  const polygons = geometry.getPolygons();
  return polygons.every(polygon => isValidPolygon(polygon)) && !doPolygonsIntersectEachOther(polygons);
}

function isValidPolygon (polygon: Polygon) {
  return turfIsValid(toTurfPolygon(polygon)) && !hasSelfIntersectingLinearRings(polygon);
}

function hasSelfIntersectingLinearRings (polygon: Polygon) {
  return polygon.getLinearRings()
    .map(ring => toTurfPolygon(new Polygon([ ring.getCoordinates() ])))
    .some(turfPolygon => turfKinks(turfPolygon).features.length > 0);
}

function doPolygonsIntersectEachOther (polygons: Polygon[]) {
  const turfPolygons = polygons.map(polygon => toTurfPolygon(polygon));
  return doTurfPolygonsIntersect(turfPolygons);
}

function doInnerLinearRingsIntersect (feature: Polygon | MultiPolygon) {
  if (feature instanceof Polygon) {
    return doPolygonInnerLinearRingsIntersect(feature);
  }
  const polygons = feature.getPolygons();
  return polygons.some(doPolygonInnerLinearRingsIntersect);
}

function doPolygonInnerLinearRingsIntersect (polygon: Polygon) {
  const turfLinearRingPolygons = polygon.getLinearRings()
    .map(linearRing => turfGeometry(GeometryType.POLYGON, [ linearRing.getCoordinates() ]) as TurfPolygon);

  turfLinearRingPolygons.shift(); // The first ring is the outer ring, we don't care about it

  return doTurfPolygonsIntersect(turfLinearRingPolygons);
}

function doTurfPolygonsIntersect (turfPolygons: TurfPolygon[]) {
  if (turfPolygons.length > 1) {
    for (let i = 0; i < turfPolygons.length - 1; i++) {
      const turfPol1 = turfPolygons[i];
      for (let j = i + 1; j < turfPolygons.length; j++) {
        const turfPol2 = turfPolygons[j];
        if (turfIntersect(turfPol1, turfPol2)) {
          return true;
        }
      }
    }
  }
  return false;
}
